<script setup lang="ts">
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useRiskStore } from '@/stores/riskData'
import { toFixedPrice } from '@/composables/commonFunctions'
import AtlasButton from '../Common/AtlasButton.vue';

const { riskData } = storeToRefs(useRiskStore())

const props = defineProps<{
    scheme?: any
    action?: string
    loading?: boolean
    hideProceed?: boolean
}>()

const emit = defineEmits<{
    (e: 'nav-next'): void
}>()

const showDetails = ref(true)

const isMta = computed(() => (props.action ?? '').toLowerCase().includes('mta'))

const premium = computed(() => isMta.value ? props.scheme?.ProRataPremium : props.scheme?.QuotedPremium)

const insurerPremium = computed(() => {
    let total = 0
    for (const cover of props.scheme?.Covers ?? []) {
        if (cover.IsRefund) continue
        const p = isMta.value ? cover.ProRataPremium : cover.QuotedPremium
        total += parseFloat(p?.BaseTotalPayable ?? 0) - parseFloat(p?.OriginalAdminFee ?? 0)
    }
    return total
})

// MTAs can produce refund covers. The scheme ProRataPremium (which drives the Total)
// already includes them, so surface them as their own line to keep the breakdown
// reconciling instead of dropping them from the itemisation.
const refundTotal = computed(() => {
    let total = 0
    for (const cover of props.scheme?.Covers ?? []) {
        if (!cover.IsRefund) continue
        const p = isMta.value ? cover.ProRataPremium : cover.QuotedPremium
        total += parseFloat(p?.BaseTotalPayable ?? 0) - parseFloat(p?.OriginalAdminFee ?? 0)
    }
    return total
})

const adminFee = computed(() => parseFloat(premium.value?.OriginalAdminFee ?? 0))

const membershipType = computed(() => riskData.value?.AdditionalFormData?.membershipType ?? null)
const paymentType = computed(() => riskData.value?.AdditionalFormData?.paymentType ?? null)
const isBroker = computed(() => membershipType.value === 'broker')
const isOnAccount = computed(() => isBroker.value && paymentType.value === 'OnAccount')

const brokerFeeLabel = computed(() => isOnAccount.value ? 'Agent admin' : 'Commission')
const brokerFee = computed(() =>
{
    if (!isBroker.value) return 0
    return parseFloat(
        (isOnAccount.value ? props.scheme?.AgentAdminFee : props.scheme?.AgentCommissionPremium) ?? 0)
})

const schemeBaseTotal = computed(() => parseFloat(premium.value?.BaseTotalPayable ?? 0))

const selectedAddonsTotal = computed(() => {
    let total = 0
    for (const addon of props.scheme?.Addons ?? []) {
        if (addon.IsSelected && !addon.IsIncluded) {
            total += parseFloat(addon.TotalPayable ?? 0)
        }
    }
    for (const group of props.scheme?.GroupedAddons ?? []) {
        for (const addon of group.GroupAddons ?? []) {
            if (addon.IsSelected && !addon.IsIncluded) {
                total += parseFloat(addon.TotalPayable ?? 0)
            }
        }
    }
    return total
})

const hasDiscount = computed(() => !!(premium.value?.PromotionApplied || premium.value?.CommissionFlexApplied))

const discountTotal = computed(() => {
    if (!hasDiscount.value) return 0
    let total = parseFloat(premium.value?.TotalDiscountApplied ?? 0)
    for (const addon of props.scheme?.Addons ?? []) {
        if (addon.IsSelected && addon.IsDiscountApplied) {
            total += parseFloat(addon.PromoDiscount ?? 0)
        }
    }
    for (const group of props.scheme?.GroupedAddons ?? []) {
        for (const addon of group.GroupAddons ?? []) {
            if (addon.IsSelected && addon.IsDiscountApplied) {
                total += parseFloat(addon.PromoDiscount ?? 0)
            }
        }
    }
    return total
})

const totalPayable = computed(() => parseFloat(premium.value?.TotalPayable ?? 0) + selectedAddonsTotal.value)

const schemeTitle = computed(() => {
    const insurer = props.scheme?.Insurer?.Description ?? ''
    const desc = props.scheme?.SchemeDescription ?? props.scheme?.Name ?? 'Scheme'
    return [insurer, desc].filter(Boolean).join(' ') || 'Scheme'
})
</script>

<template>
    <div class="card mB-10 w-100 h-100 position-relative">
        <div v-if="loading" class="position-absolute w-100 h-100 d-flex align-items-center justify-content-center" style="inset: 0; background: rgba(255, 255, 255, 0.6); z-index: 10;">
            <span class="spinner-border text-info" role="status" aria-hidden="true"></span>
        </div>
        <div class="card-header bg-transparent d-flex justify-content-between align-items-center">
            <h5 class="card-title mB-0">{{ schemeTitle }}</h5>
            <a
                href="#"
                class="small text-primary text-nowrap"
                @click.prevent="showDetails = !showDetails"
            >
                {{ showDetails ? 'Hide details' : 'Show details' }}
            </a>
        </div>
        <div class="card-body">
            <div v-if="showDetails" class="small mb-3">
                <div class="d-flex justify-content-between border-bottom py-1">
                    <span>Insurer Premium</span>
                    <span>{{ toFixedPrice(insurerPremium) }}</span>
                </div>
                <div v-if="refundTotal !== 0" class="d-flex justify-content-between border-bottom py-1">
                    <span>Refund</span>
                    <span class="c-danger">{{ toFixedPrice(refundTotal) }}</span>
                </div>
                <div class="d-flex justify-content-between border-bottom py-1">
                    <span>Admin Fee</span>
                    <span>{{ toFixedPrice(adminFee) }}</span>
                </div>
                <div v-if="brokerFee > 0" class="d-flex justify-content-between border-bottom py-1">
                    <span>{{ brokerFeeLabel }}</span>
                    <span>{{ toFixedPrice(brokerFee) }}</span>
                </div>
                <div class="d-flex justify-content-between border-bottom py-1">
                    <span>Total</span>
                    <span>{{ toFixedPrice(schemeBaseTotal) }}</span>
                </div>
                <div v-if="selectedAddonsTotal > 0" class="d-flex justify-content-between border-bottom py-1">
                    <span>Add-ons</span>
                    <span>{{ toFixedPrice(selectedAddonsTotal) }}</span>
                </div>
                <div v-if="hasDiscount" class="d-flex justify-content-between border-bottom py-1">
                    <span>Discount Calc</span>
                    <span>{{ toFixedPrice(-discountTotal) }}</span>
                </div>
            </div>

            <div class="d-flex align-items-baseline justify-content-between">
                <div class="d-flex align-items-baseline">
                    <h3 class="mr-3 mb-2 mb-sm-0">{{ toFixedPrice(totalPayable) }}</h3>
                    <div class="small text-muted">Total payable.</div>
                </div>

                <div v-if="props.scheme?.IsSelected && !props.hideProceed">
                    <AtlasButton
                        variant="primary"
                        @click="emit('nav-next')"
                    >
                        Proceed
                    </AtlasButton>
                </div>
            </div>
        </div>
    </div>
</template>
